import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import {
  FaShieldAlt, FaCheckCircle, FaClock, FaTimesCircle, FaCloudUploadAlt,
  FaIdCard, FaFileAlt, FaTrash, FaSpinner, FaLock, FaUserCheck,
  FaSearch, FaStar, FaExclamationTriangle
} from 'react-icons/fa';
import { toast } from 'react-toastify';
import { useAuth } from '../context/AuthContext';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

// Upload Card
const UploadCard = ({ title, description, icon: Icon, file, preview, onUpload, onRemove, disabled }) => {
  const inputRef = useRef(null);

  const handleFileChange = (e) => {
    const selected = e.target.files?.[0];
    if (selected) {
      if (selected.size > 5 * 1024 * 1024) {
        toast.error('फाइल 5MB पेक्षा कमी असावी');
        return;
      }
      onUpload(selected);
    }
  };

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
      <div className="bg-gradient-to-r from-[#8B0000] to-[#DC143C] px-5 py-3 flex items-center gap-2">
        <Icon className="text-[#FFD700]" />
        <h3 className="text-white font-semibold font-['Noto_Sans_Devanagari'] text-sm">{title}</h3>
      </div>

      <div className="p-5">
        <p className="text-sm text-gray-500 mb-4 font-['Noto_Sans_Devanagari']">{description}</p>

        {preview || file ? (
          <div className="relative group">
            <img
              src={preview || file}
              alt={title}
              className="w-full h-48 object-contain bg-gray-50 rounded-lg border border-gray-200"
            />
            {!disabled && (
              <button
                onClick={onRemove}
                className="absolute top-2 right-2 bg-red-500 text-white p-2 rounded-full opacity-0 group-hover:opacity-100 transition-opacity shadow-lg hover:bg-red-600"
              >
                <FaTrash className="text-xs" />
              </button>
            )}
            <div className="absolute bottom-2 left-2 bg-green-500 text-white text-xs px-2 py-1 rounded-full flex items-center gap-1">
              <FaCheckCircle className="text-[10px]" /> अपलोड झाले
            </div>
          </div>
        ) : (
          <div
            onClick={() => !disabled && inputRef.current?.click()}
            className={`border-2 border-dashed rounded-xl p-8 text-center transition-all ${
              disabled
                ? 'border-gray-200 cursor-not-allowed opacity-60'
                : 'border-[#8B0000]/30 cursor-pointer hover:border-[#DC143C] hover:bg-[#DC143C]/5'
            }`}
          >
            <FaCloudUploadAlt className="text-3xl text-[#8B0000] mx-auto mb-3" />
            <p className="text-[#8B0000] font-medium text-sm font-['Noto_Sans_Devanagari']">
              फोटो अपलोड करण्यासाठी क्लिक करा
            </p>
            <p className="text-xs text-gray-400 mt-1">JPG, PNG - Max 5MB</p>
          </div>
        )}

        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          onChange={handleFileChange}
          className="hidden"
        />
      </div>
    </div>
  );
};

// Status Component
const VerificationStatus = ({ status, rejectionReason }) => {
  const statuses = {
    not_submitted: {
      icon: FaExclamationTriangle,
      color: 'text-gray-500',
      bgColor: 'bg-gray-100',
      borderColor: 'border-gray-200',
      label: 'सबमिट केलेले नाही',
      sublabel: 'कृपया आवश्यक कागदपत्रे अपलोड करा',
    },
    pending: {
      icon: FaClock,
      color: 'text-yellow-600',
      bgColor: 'bg-yellow-50',
      borderColor: 'border-yellow-200',
      label: 'पडताळणी प्रक्रियेत',
      sublabel: 'तुमची कागदपत्रे तपासली जात आहेत. कृपया 24-48 तास प्रतीक्षा करा.',
    },
    approved: {
      icon: FaCheckCircle,
      color: 'text-green-600',
      bgColor: 'bg-green-50',
      borderColor: 'border-green-200',
      label: 'सत्यापित',
      sublabel: 'तुमची ओळख यशस्वीरित्या पडताळणी झाली आहे!',
    },
    rejected: {
      icon: FaTimesCircle,
      color: 'text-red-600',
      bgColor: 'bg-red-50',
      borderColor: 'border-red-200',
      label: 'नाकारले',
      sublabel: rejectionReason || 'कृपया स्पष्ट कागदपत्रे पुन्हा अपलोड करा.',
    }
  };

  const s = statuses[status] || statuses.not_submitted;

  return (
    <div className={`${s.bgColor} border ${s.borderColor} rounded-xl p-5 flex items-start gap-4`}>
      <div className={`w-12 h-12 ${s.bgColor} rounded-full flex items-center justify-center flex-shrink-0`}>
        <s.icon className={`${s.color} text-2xl`} />
      </div>
      <div>
        <h3 className={`font-bold ${s.color} font-['Noto_Sans_Devanagari'] text-lg`}>{s.label}</h3>
        <p className="text-sm text-gray-600 mt-1 font-['Noto_Sans_Devanagari']">{s.sublabel}</p>
      </div>
    </div>
  );
};

const AadhaarVerificationPage = () => {
  const { api, user } = useAuth();
  const [verificationStatus, setVerificationStatus] = useState('not_submitted');
  const [rejectionReason, setRejectionReason] = useState('');
  const [aadhaarFile, setAadhaarFile] = useState(null);
  const [aadhaarPreview, setAadhaarPreview] = useState(null);
  const [panFile, setPanFile] = useState(null);
  const [panPreview, setPanPreview] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchVerificationStatus();
  }, []);

  const fetchVerificationStatus = async () => {
    setLoading(true);
    try {
      const response = await api.get('/api/profiles/verification-status');
      const data = response.data;
      setVerificationStatus(data.status || data.verificationStatus || 'not_submitted');
      setRejectionReason(data.rejectionReason || '');
      if (data.aadhaarPhoto) setAadhaarPreview(data.aadhaarPhoto);
      if (data.panPhoto) setPanPreview(data.panPhoto);
    } catch (error) {
      console.error('Failed to fetch verification status:', error);
      // Default to not_submitted
      setVerificationStatus(user?.isVerified ? 'approved' : 'not_submitted');
    } finally {
      setLoading(false);
    }
  };

  const handleAadhaarUpload = (file) => {
    setAadhaarFile(file);
    setAadhaarPreview(URL.createObjectURL(file));
  };

  const handlePanUpload = (file) => {
    setPanFile(file);
    setPanPreview(URL.createObjectURL(file));
  };

  const handleAadhaarRemove = () => {
    setAadhaarFile(null);
    setAadhaarPreview(null);
  };

  const handlePanRemove = () => {
    setPanFile(null);
    setPanPreview(null);
  };

  const handleSubmit = async () => {
    if (!aadhaarFile && !aadhaarPreview) {
      toast.error('कृपया आधार कार्ड फोटो अपलोड करा');
      return;
    }

    setSubmitting(true);
    try {
      const formData = new FormData();
      if (aadhaarFile) formData.append('aadhaar', aadhaarFile);
      if (panFile) formData.append('pan', panFile);

      await api.post('/api/profiles/aadhaar-upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      setVerificationStatus('pending');
      toast.success('कागदपत्रे यशस्वीरित्या सबमिट केली!');
    } catch (error) {
      console.error('Failed to submit documents:', error);
      toast.error('सबमिट करण्यात अयशस्वी. कृपया पुन्हा प्रयत्न करा.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleRetry = () => {
    setVerificationStatus('not_submitted');
    setAadhaarFile(null);
    setAadhaarPreview(null);
    setPanFile(null);
    setPanPreview(null);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FFF8F0]">
        <Navbar />
        <div className="max-w-3xl mx-auto px-4 py-12 animate-pulse space-y-6">
          <div className="h-8 bg-gray-200 rounded w-64" />
          <div className="h-24 bg-gray-200 rounded-xl" />
          <div className="h-64 bg-gray-200 rounded-xl" />
          <div className="h-64 bg-gray-200 rounded-xl" />
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FFF8F0] flex flex-col">
      <Navbar />

      {/* Header */}
      <div className="bg-gradient-to-r from-[#8B0000] to-[#DC143C] py-8">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-[#FFD700]/20 rounded-full flex items-center justify-center">
              <FaShieldAlt className="text-[#FFD700] text-xl" />
            </div>
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-white font-['Noto_Sans_Devanagari']">
                ओळख पडताळणी
              </h1>
              <p className="text-[#FFD700]/80 text-sm font-['Playfair_Display']">Identity Verification</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-6 flex-1">
        {/* Why Verification */}
        <div className="bg-white rounded-xl shadow-sm p-5 border border-gray-100 mb-6">
          <h3 className="text-lg font-bold text-[#8B0000] font-['Noto_Sans_Devanagari'] mb-3">
            पडताळणी का महत्त्वाची आहे?
          </h3>
          <p className="text-sm text-gray-600 leading-relaxed font-['Noto_Sans_Devanagari'] mb-4">
            ओळख पडताळणी केल्याने तुमचे प्रोफाइल विश्वसनीय बनते आणि इतर सदस्यांना तुमच्याशी जोडण्यास अधिक आत्मविश्वास मिळतो.
            पडताळणी प्रक्रिया पूर्णपणे गोपनीय आहे.
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {[
              { icon: FaUserCheck, label: 'सत्यापित बॅज', desc: 'प्रोफाइलवर विश्वसनीयता बॅज' },
              { icon: FaSearch, label: 'सर्चमध्ये प्राधान्य', desc: 'सत्यापित प्रोफाइल वर दिसतात' },
              { icon: FaStar, label: 'जास्त प्रतिसाद', desc: '3x अधिक रस मिळतो' },
            ].map((item, idx) => (
              <div key={idx} className="bg-[#FFF8F0] rounded-lg p-3 text-center">
                <item.icon className="text-[#8B0000] text-xl mx-auto mb-2" />
                <p className="font-semibold text-[#8B0000] text-sm font-['Noto_Sans_Devanagari']">{item.label}</p>
                <p className="text-xs text-gray-500 mt-1 font-['Noto_Sans_Devanagari']">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Status */}
        <div className="mb-6">
          <VerificationStatus status={verificationStatus} rejectionReason={rejectionReason} />
        </div>

        {/* Upload Section - show for not_submitted and rejected */}
        {(verificationStatus === 'not_submitted' || verificationStatus === 'rejected') && (
          <div className="space-y-6">
            {verificationStatus === 'rejected' && (
              <button
                onClick={handleRetry}
                className="text-sm text-[#DC143C] hover:underline font-['Noto_Sans_Devanagari']"
              >
                पुन्हा प्रयत्न करा →
              </button>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <UploadCard
                title="आधार कार्ड (पुढील बाजू)"
                description="आधार कार्डचा स्पष्ट फोटो अपलोड करा"
                icon={FaIdCard}
                file={null}
                preview={aadhaarPreview}
                onUpload={handleAadhaarUpload}
                onRemove={handleAadhaarRemove}
                disabled={false}
              />
              <UploadCard
                title="पॅन कार्ड"
                description="पॅन कार्डचा स्पष्ट फोटो अपलोड करा"
                icon={FaFileAlt}
                file={null}
                preview={panPreview}
                onUpload={handlePanUpload}
                onRemove={handlePanRemove}
                disabled={false}
              />
            </div>

            {/* Privacy Note */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-start gap-3">
              <FaLock className="text-blue-500 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-sm text-blue-800 font-medium font-['Noto_Sans_Devanagari']">गोपनीयता हमी</p>
                <p className="text-xs text-blue-600 mt-1 font-['Noto_Sans_Devanagari']">
                  तुमची कागदपत्रे फक्त पडताळणीसाठी वापरली जातात. इतर कोणत्याही सदस्यांना दाखवली जात नाहीत. सर्व डेटा एन्क्रिप्टेड आहे.
                </p>
              </div>
            </div>

            {/* Submit Button */}
            <button
              onClick={handleSubmit}
              disabled={submitting || (!aadhaarFile && !aadhaarPreview)}
              className="w-full bg-gradient-to-r from-[#8B0000] to-[#DC143C] text-white py-3.5 rounded-xl font-bold text-sm hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed font-['Noto_Sans_Devanagari']"
            >
              {submitting ? (
                <span className="flex items-center justify-center gap-2">
                  <FaSpinner className="animate-spin" /> सबमिट करत आहे...
                </span>
              ) : (
                'कागदपत्रे सबमिट करा'
              )}
            </button>
          </div>
        )}

        {/* Pending State */}
        {verificationStatus === 'pending' && (
          <div className="text-center py-8">
            <div className="w-20 h-20 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <FaClock className="text-yellow-600 text-3xl animate-pulse" />
            </div>
            <h3 className="text-lg font-bold text-gray-800 font-['Noto_Sans_Devanagari'] mb-2">
              तुमची पडताळणी प्रक्रियेत आहे
            </h3>
            <p className="text-sm text-gray-500 font-['Noto_Sans_Devanagari'] max-w-md mx-auto">
              आमची टीम तुमची कागदपत्रे तपासत आहे. साधारणपणे 24-48 तासांत पडताळणी पूर्ण होते. तुम्हाला ईमेल/SMS द्वारे सूचित केले जाईल.
            </p>
          </div>
        )}

        {/* Approved State */}
        {verificationStatus === 'approved' && (
          <div className="text-center py-8">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <FaCheckCircle className="text-green-600 text-3xl" />
            </div>
            <h3 className="text-lg font-bold text-green-700 font-['Noto_Sans_Devanagari'] mb-2">
              तुमची ओळख सत्यापित आहे!
            </h3>
            <p className="text-sm text-gray-500 font-['Noto_Sans_Devanagari'] max-w-md mx-auto mb-6">
              तुमच्या प्रोफाइलवर सत्यापित बॅज दिसत आहे. तुम्हाला सर्चमध्ये प्राधान्य मिळेल.
            </p>
            <div className="inline-flex items-center gap-2 bg-green-100 text-green-700 px-4 py-2 rounded-full font-medium text-sm">
              <FaCheckCircle /> Verified Profile
            </div>
          </div>
        )}
      </div>

      <Footer />
    </div>
  );
};

export default AadhaarVerificationPage;
